import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { Col, Row } from 'react-flexbox-grid';
import { injectIntl } from 'react-intl';
import moment from 'moment';
import classnames from 'classnames';
import { withStyles } from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';

import { Icon, FormFieldError, Label, Loader, Autocomplete } from 'shared/components';
import { isCurrentLanguageRtl } from 'modules/language/selectors/language';
import { AccessibilityContext } from 'App';

import requestsStatuses from 'modules/common/constants/requestsStatuses';

import theme from './DateTimeInput.scss';
import { styles } from './styles';

const getDefaultOptions = (interval) => {
  const options = [];
  for (let i = 0; i < 24; i += interval) {
    options.push({
      key: i,
      value: moment({ hour: Math.floor(i), minute: (i % 1) * 60 }).format('HH:mm'),
    });
  }

  options.push({
    key: 23.99,
    value: moment({ hour: 23, minute: 59 }).format('HH:mm'),
  });

  return options;
};

const propTypes = {
  status: PropTypes.oneOf(Object.values(requestsStatuses)),
  intl: PropTypes.object.isRequired,
  label: PropTypes.object,
  datePlaceholder: PropTypes.object,
  timePlaceholder: PropTypes.object,
  date: PropTypes.any,
  dateFormat: PropTypes.string,
  setDate: PropTypes.any.isRequired,
  time: PropTypes.any,
  setTime: PropTypes.any.isRequired,
  disableTimeSelection: PropTypes.bool,
  datepickerProps: PropTypes.object,
  tooltipType: PropTypes.oneOf(['text', 'hint', 'intl']),
  tooltipTestId: PropTypes.string,
  tooltipMessage: PropTypes.object,
  tooltipSize: PropTypes.oneOf(['medium', 'large']),
  tooltipWidth: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  tooltipPlacement: PropTypes.string,
  clearable: PropTypes.bool,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  pickerClassName: PropTypes.string,
  syncedSelection: PropTypes.bool,
  className: PropTypes.string,
  reversedSelection: PropTypes.bool,
  optionsInterval: PropTypes.number,
  formatInputChange: PropTypes.func,
  popoverRootClassname: PropTypes.string,
  items: PropTypes.array,
  rootClassName: PropTypes.string,
};

const defaultProps = {
  status: requestsStatuses.default,
  label: null,
  datePlaceholder: {
    id: 'AdminConsole.Appointments.Create.Dialog.DateAndTime.Date.Placeholder',
    defaultMessage: '** dd/mm/yyyy',
  },
  date: null,
  dateFormat: 'DD/MM/YYYY',
  time: null,
  timePlaceholder: {
    id: 'AdminConsole.Appointments.Create.Dialog.DateAndTime.Time.Placeholder',
    defaultMessage: '** hh:mm',
  },
  disableTimeSelection: false,
  datepickerProps: {},
  tooltipType: 'intl',
  tooltipTestId: 'tooltip',
  tooltipMessage: null,
  tooltipSize: 'large',
  tooltipWidth: 'fit-content',
  tooltipPlacement: 'bottomStart',
  clearable: false,
  disabled: false,
  loading: false,
  pickerClassName: '',
  syncedSelection: true,
  className: '',
  reversedSelection: false,
  optionsInterval: 0.5,
  formatInputChange: value => value,
  popoverRootClassname: '',
  items: null,
  rootClassName: '',
};

const DateTimeInput = ({
  id,
  name,
  intl,
  date,
  setDate,
  time,
  setTime,
  disableTimeSelection,
  getOptions = getDefaultOptions,
  items,
  optionsInterval,
  label,
  datePlaceholder,
  dateFormat,
  timePlaceholder,
  datepickerProps,
  tooltipType,
  tooltipTestId,
  tooltipMessage,
  tooltipSize,
  tooltipWidth,
  tooltipPlacement,
  classes,
  clearable,
  disabled,
  loading,
  pickerClassName,
  syncedSelection,
  className,
  reversedSelection,
  timeTooltipMessage,
  timeStatus,
  onBlur,
  errorClassName,
  formatInputChange,
  popoverRootClassname,
  rootClassName,
}) => {
  useEffect(() => {
    document.querySelector(`input[name="${ name }"]`)
      ?.setAttribute('aria-label', `${ intl.formatMessage(label) }, , Click to open a date picker`);
  });

  const { isAccessibilityEnabled } = useContext(AccessibilityContext);

  const dialogProps = {
    clearable,
    clearLabel: <Label label={{ id: 'Operations.Date.Reset', defaultMessage: '** Reset' }} className={ classnames(theme.calendarControl, theme.controlCancel) } />,
    cancelLabel: <Label label={{ id: 'General.Cancel', defaultMessage: '** Cancel' }} className={ classnames(theme.calendarControl, theme.controlCancel) } />,
    okLabel: <Label label={{ id: 'General.Ok', defaultMessage: '** OK' }} className={ theme.calendarControl } />,
    DialogProps: {
      className: classnames({ rtl: isCurrentLanguageRtl() }),
      classes: {
        withAdditionalAction: theme.dialogAction,
      },
    },
  };

  const inlineProps = {
    PopoverProps: {
      className: classnames(theme.popover, popoverRootClassname, { rtl: isCurrentLanguageRtl() }),
      classes: {
        paper: theme.popover,
      },
    },
  };

  return (
    <Row className={ classnames(rootClassName, { [theme.reversed]: reversedSelection }) }>
      <Col
        xs={ disableTimeSelection ? 12 : 7 }
        className={ classnames(theme.fixWidth, className) }
      >
        <If condition={ !reversedSelection }>
          <Label
            label={ label }
            className={ theme.label }
            htmlFor={ id || name }
          />
        </If>
        <DatePicker
          name={ name }
          className={ classnames(pickerClassName, { rtl: isCurrentLanguageRtl() }) }
          disabled={ disabled }
          value={ date }
          onChange={ setDate }
          format={ dateFormat }
          helperText={ null }
          emptyLabel={ intl.formatMessage(datePlaceholder) }
          InputProps={{
            id: id || name,
            classes: {
              root: `${ classes.inputRoot } ${ !date ? classes.inputEmpty : '' } ${ tooltipMessage ? classes.inputError : '' } ${ disabled ? classes.disabled : '' } ${ reversedSelection && label ? theme.fixMargin : '' }`, //eslint-disable-line
              input: disabled ? (isAccessibilityEnabled ? classes.disabledWithAccessibility : classes.disabled) : '',
            },
            startAdornment: <Icon name="calendar" className={ classnames(theme.icon, theme.iconLeft, { [theme.colored]: !!date, [theme.disabled]: disabled }) } />,
            endAdornment: (
              <div className={ theme.iconsWrap }>
                <If condition={ loading }>
                  <div className={ theme.loader }><Loader /></div>
                </If>
                <Icon name="dropdown-chevron-fill" className={ classnames(theme.icon, theme.controlCancel, { [theme.disabled]: disabled }) } />
              </div>
            ),
          }}
          leftArrowButtonProps={{
            'aria-label': 'Go to the previous month',
          }}
          rightArrowButtonProps={{
            'aria-label': 'Go to the next month',
          }}
          { ...datepickerProps.variant !== 'inline' ? dialogProps : inlineProps }
          { ...datepickerProps }
        />
        <div className={ theme.error }>
          <FormFieldError
            tooltipType={ tooltipType }
            testId={ tooltipTestId }
            width={ tooltipWidth }
            tooltipMessage={ tooltipMessage }
            tooltipSize={ tooltipSize }
            tooltipPlacement={ tooltipPlacement }
            className={ errorClassName }
          />
        </div>
      </Col>

      <If condition={ !disableTimeSelection }>
        <Col xs={ 5 }>
          <Autocomplete
            id={ id || name }
            name={ name }
            testId="date-range-time-from"
            selectedItem={ time }
            items={ items || getOptions(optionsInterval) }
            disabled={ (!date && syncedSelection) || disabled }
            placeholder={ timePlaceholder }
            iconName="clock"
            size="small"
            label={ reversedSelection && label ? label : null }
            onChange={ setTime }
            onBlur={ onBlur }
            visibleItemsAmount={ 6 }
            overlay
            tooltipMessage={ timeTooltipMessage }
            tooltipSize={ tooltipSize }
            tooltipPlacement={ tooltipPlacement }
            tooltipTestId={ tooltipTestId }
            status={ timeStatus }
            rootClassName={ !reversedSelection ? theme.fixMargin : '' }
            errorClassName={ errorClassName }
            formatInputChange={ formatInputChange }
          />
        </Col>
      </If>
    </Row>
  );
};

DateTimeInput.propTypes = propTypes;
DateTimeInput.defaultProps = defaultProps;

export default compose(
  injectIntl,
  withStyles(styles),
)(DateTimeInput);
